/**
 * Role vocabulary for the BurgePOS management application.
 *
 * The four personas come from `docs/PRD.md` section 4. They are named here so
 * that navigation and route definitions can talk about roles without inventing
 * a role set of their own. `support` is a named BurgePOS support operator
 * (ADR 0007; P1-013 slice 5), drawn from the session's SUPPORT actor class: it
 * holds no shop role and sees a shop only under an Admin's grant.
 *
 * This module describes *who a person claims to be*. It never decides what a
 * person is allowed to do. Authorization is a trusted server decision and is
 * owned by ticket P1-014.
 */

export const PLATFORM_ROLES = ["cashier", "manager", "admin", "superadmin", "support"] as const;

export type PlatformRole = (typeof PLATFORM_ROLES)[number];

export const ROLE_LABELS: Record<PlatformRole, string> = {
  cashier: "Cashier",
  manager: "Manager",
  admin: "Admin (shop owner)",
  superadmin: "Platform superadmin",
  support: "BurgePOS support",
};

/**
 * PRD section 4: a cashier sells, takes payment and runs their own shift on the
 * Windows till. BurgePOS v1 gives cashiers no web workspace, and PRD section
 * 6.6 keeps the management login separate from till credentials. A cashier
 * arriving here is therefore a routing outcome with its own explanation rather
 * than a generic "forbidden".
 */
export const TILL_ONLY_ROLES: readonly PlatformRole[] = ["cashier"];

/** Roles that have a web workspace in v1, derived so the two lists cannot drift. */
export const WEB_WORKSPACE_ROLES: readonly PlatformRole[] = PLATFORM_ROLES.filter(
  (role) => !TILL_ONLY_ROLES.includes(role),
);

export function isPlatformRole(value: unknown): value is PlatformRole {
  return typeof value === "string" && (PLATFORM_ROLES as readonly string[]).includes(value);
}

export function isTillOnlyRole(role: PlatformRole): boolean {
  return TILL_ONLY_ROLES.includes(role);
}
